import { ChevronDownIcon } from "@chakra-ui/icons";
import {
	Button,
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerOverlay,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	useBreakpointValue,
	useDisclosure,
} from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { useState } from "react";
import { cn } from "utils/helpers";
import { generatePdf } from "./api-PdfDownload";

interface PdfDownloadButtonProps {
	className?: string;
	rfxId: number;
	respondentId?: number;
	isGuest?: boolean;
	menuText?: string;
	showDownloadSummary?: boolean;
}

export const PdfDownloadButton: React.FC<PdfDownloadButtonProps> = ({
	className,
	rfxId,
	menuText = "RFx PDF Download",
	respondentId,
	isGuest,
	showDownloadSummary = true,
}) => {
	const isMobile = useBreakpointValue({ base: true, md: false });
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [isLoading, setIsLoading] = useState(false);

	if (!rfxId) {
		return null;
	}

	const handleDownload = (type: "summary" | "responses") => {
		setIsLoading(true);

		generatePdf(type, rfxId, respondentId, isGuest).finally(() =>
			setIsLoading(false)
		);
	};

	const summaryText = `${isMobile ? "Download " : ""}RFx Summary PDF`;
	const responsesText = `${isMobile ? "Download " : ""}All RFx Responses PDF`;
	const loadingText = "Generating, please wait...";

	if (isMobile && showDownloadSummary) {
		// Render a drawer on mobile
		return (
			<>
				<Button
					w="full"
					onClick={onOpen}
					className={`bg-bg-brand-solid text-white ${className}`}
					loadingText={loadingText}
					isLoading={isLoading}
				>
					{menuText}
				</Button>

				<Drawer
					isOpen={isOpen}
					placement="top"
					onClose={onClose}
				>
					<DrawerOverlay />
					<DrawerContent>
						<DrawerBody p={4}>
							<Button
								w="full"
								mb={2}
								onClick={() => handleDownload("summary")}
								isLoading={isLoading}
								loadingText={loadingText}
							>
								{summaryText}
							</Button>
							<Button
								w="full"
								mb={2}
								onClick={() => handleDownload("responses")}
								isLoading={isLoading}
								loadingText={loadingText}
							>
								{showDownloadSummary ? responsesText : menuText}
							</Button>
						</DrawerBody>
					</DrawerContent>
				</Drawer>
			</>
		);
	}

	if (!showDownloadSummary) {
		return (
			<Button
				onClick={() => handleDownload("responses")}
				isLoading={isLoading}
				loadingText={loadingText}
				className={cn(
					"mb-4 bg-bg-brand-solid text-white hover:bg-blue-600",
					isMobile && "!rounded-none",
					className
				)}
			>
				<Icon
					icon="download"
					className="pr-2"
				/>
				{menuText}
			</Button>
		);
	}

	return (
		<Menu>
			<MenuButton
				as={Button}
				rightIcon={<ChevronDownIcon />}
				className={`bg-bg-brand-solid text-white ${className}`}
				minW="max-content"
				w={{ base: "full", md: "auto" }}
				loadingText={loadingText}
				isLoading={isLoading}
			>
				{menuText}
			</MenuButton>
			<MenuList
				w={{ base: "full", md: "auto" }}
				minW={0}
			>
				<MenuItem
					w="full"
					onClick={() => handleDownload("summary")}
				>
					{summaryText}
				</MenuItem>
				<MenuItem
					w="full"
					onClick={() => handleDownload("responses")}
				>
					{responsesText}
				</MenuItem>
			</MenuList>
		</Menu>
	);
};
